import { useState, useCallback, useRef } from 'react';
import { validatePhoneNumber, validateEmail } from '../../../util/ValidationUtils';
import { ThirdPartyFormData, INITIAL_FORM_DATA } from '../types';

interface UseTokenFormOptions {
  initialData?: ThirdPartyFormData;
  onDirtyChange?: (isDirty: boolean) => void;
}

interface UseTokenFormReturn {
  formData: ThirdPartyFormData;
  emailErrorMsg: string;
  firstNameErrorMsg: string;
  lastNameErrorMsg: string;
  phoneNumberErrorMsg: string;
  isFormValid: boolean;
  handleFieldChange: (field: keyof ThirdPartyFormData) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlePhoneNumberChange: (value: string | undefined) => void;
  resetForm: (data?: ThirdPartyFormData, validateOnReset?: boolean) => void;
  getSubmitPayload: () => {
    description: string | null;
    email: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    company_name: string | null;
  };
}

/**
 * Custom hook for managing API token form state and validation.
 * Shared between Create and Edit flows.
 */
export const useTokenForm = (options: UseTokenFormOptions = {}): UseTokenFormReturn => {
  const { initialData = INITIAL_FORM_DATA, onDirtyChange } = options;

  // Track the baseline for dirty-state comparison. Updated when resetForm is called.
  const baselineDataRef = useRef<ThirdPartyFormData>(initialData);

  const [formData, setFormData] = useState<ThirdPartyFormData>(initialData);
  const [emailErrorMsg, setEmailErrorMsg] = useState('');
  const [firstNameErrorMsg, setFirstNameErrorMsg] = useState('');
  const [lastNameErrorMsg, setLastNameErrorMsg] = useState('');
  const [phoneNumberErrorMsg, setPhoneNumberErrorMsg] = useState('');

  // Validation helpers
  const phoneNumberValidation = (value: string): string => {
    if (!value || !value.trim()) return 'Please enter a phone number.';
    return validatePhoneNumber(value, true) ? '' : 'Phone number must be 10 digits.';
  };

  const emailValidation = (value: string): string => {
    if (!value || !value.trim()) return 'Please enter an email address.';
    return validateEmail(value) ? '' : 'Please enter a valid email address.';
  };

  const firstNameValidation = (value: string): string => {
    if (!value || !value.trim()) return 'Please enter a first name.';
    return '';
  };

  const lastNameValidation = (value: string): string => {
    if (!value || !value.trim()) return 'Please enter a last name.';
    return '';
  };

  const isFormValid =
    formData.email.trim() !== '' &&
    !emailErrorMsg &&
    formData.firstName.trim() !== '' &&
    !firstNameErrorMsg &&
    formData.lastName.trim() !== '' &&
    !lastNameErrorMsg &&
    formData.phoneNumber.trim() !== '' &&
    !phoneNumberErrorMsg;

  // Check if form data differs from initial state
  const checkFormDataDirty = useCallback((newFormData: ThirdPartyFormData, baseData: ThirdPartyFormData): boolean => {
    return (
      newFormData.email !== baseData.email ||
      newFormData.firstName !== baseData.firstName ||
      newFormData.lastName !== baseData.lastName ||
      newFormData.phoneNumber !== baseData.phoneNumber ||
      newFormData.companyName !== baseData.companyName ||
      newFormData.description !== baseData.description
    );
  }, []);

  // Update form and notify parent of dirty state change
  const updateFormData = useCallback((newFormData: ThirdPartyFormData) => {
    setFormData(prevData => {
      const baseline = baselineDataRef.current;
      const wasDirty = checkFormDataDirty(prevData, baseline);
      const willBeDirty = checkFormDataDirty(newFormData, baseline);

      if (wasDirty !== willBeDirty && onDirtyChange) {
        onDirtyChange(willBeDirty);
      }

      return newFormData;
    });
  }, [onDirtyChange, checkFormDataDirty]);

  const handleFieldChange = useCallback((field: keyof ThirdPartyFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    updateFormData({ ...formData, [field]: value });

    if (field === 'email') {
      setEmailErrorMsg(emailValidation(value));
    } else if (field === 'firstName') {
      setFirstNameErrorMsg(firstNameValidation(value));
    } else if (field === 'lastName') {
      setLastNameErrorMsg(lastNameValidation(value));
    }
  }, [formData, updateFormData]);

  const handlePhoneNumberChange = useCallback((value: string | undefined) => {
    const phoneValue = value || '';
    updateFormData({ ...formData, phoneNumber: phoneValue });
    setPhoneNumberErrorMsg(phoneNumberValidation(phoneValue));
  }, [formData, updateFormData]);

  const resetForm = useCallback((data: ThirdPartyFormData = INITIAL_FORM_DATA, validateOnReset: boolean = false) => {
    setFormData(data);
    baselineDataRef.current = data; // Update baseline for dirty-state tracking
    if (validateOnReset) {
      // Validate initial data to show errors for empty required fields (used in Edit flow)
      setEmailErrorMsg(emailValidation(data.email));
      setFirstNameErrorMsg(firstNameValidation(data.firstName));
      setLastNameErrorMsg(lastNameValidation(data.lastName));
      setPhoneNumberErrorMsg(phoneNumberValidation(data.phoneNumber));
    } else {
      // Clear errors for fresh form (used in Create flow)
      setEmailErrorMsg('');
      setFirstNameErrorMsg('');
      setLastNameErrorMsg('');
      setPhoneNumberErrorMsg('');
    }
  }, []);

  const getSubmitPayload = useCallback(() => ({
    description: formData.description || null,
    email: formData.email,
    first_name: formData.firstName,
    last_name: formData.lastName,
    phone_number: formData.phoneNumber,
    company_name: formData.companyName || null,
  }), [formData]);

  return {
    formData,
    emailErrorMsg,
    firstNameErrorMsg,
    lastNameErrorMsg,
    phoneNumberErrorMsg,
    isFormValid,
    handleFieldChange,
    handlePhoneNumberChange,
    resetForm,
    getSubmitPayload,
  };
};
